import type { CriterionResult, GrowingConditions, PlantRequest, RequirementWeight } from './types';

export const GROWING_RULE_VERSION = 'MY-2026-09-18.1';
const FLOWERS = 'https://epengembangan.doa.gov.my/tanaman/bunga-bungaan/';
const ORCHIDS = 'https://epengembangan.doa.gov.my/tanaman/bunga-bungaan/bunga-orkid/';
const CHRYSANTHEMUMS = 'https://epengembangan.doa.gov.my/tanaman/bunga-bungaan/bunga-kekwa/';
/** Exact named cultivars from DOA guidance, not extrapolated to every rose. */
const LOWLAND_ROSES = new Set(['pink expression', 'double delight', 'slim dusty']);
export function assessPlant(plant: PlantRequest, growing: GrowingConditions, weight: Exclude<RequirementWeight, 'skip'> = 'preferred', country = 'US'): CriterionResult {
  const result: CriterionResult = {
    key: `plant:${plant.key}${plant.cultivar ? `:${plant.cultivar}` : ''}`,
    label: `Can grow ${plant.label}`, weight, status: 'unknown', evidence: 'unknown',
    reason: `We do not have enough Malaysian cultivar and site evidence for ${plant.label}.`,
  };
  if (country !== 'MY') return { ...result, reason: `Growing suitability for ${plant.label} needs local climate, soil, water and cultivar evidence. No reviewed rule for this country has been applied.` };
  const environment = growing.environment;
  if (plant.key === 'roses') {
    result.sourceUrls = [FLOWERS];
    if (!plant.cultivar || !LOWLAND_ROSES.has(plant.cultivar.toLowerCase())) {
      result.reason = 'Rose suitability depends on the cultivar and growing conditions. The requested cultivar has no reviewed site assessment.';
      return result;
    }
    if (environment.value === null || environment.kind === 'unknown') {
      result.reason = `DOA lists ${plant.cultivar} for lowland cultivation; this site's growing environment has not been verified.`;
      return result;
    }
    if (environment.value !== 'lowland') {
      result.reason = `The reviewed ${plant.cultivar} recommendation covers lowland cultivation. We have no matching highland assessment.`;
      return result;
    }
    if (growing.drainage.value === 'waterlogged' && growing.drainage.kind !== 'unknown') {
      return { ...result, status: 'not-met', evidence: growing.drainage.kind, reason: 'Reported waterlogging conflicts with the reviewed cultivation requirements.' };
    }
    const missing: string[] = [];
    if (growing.drainage.kind === 'unknown' || growing.drainage.value === null) missing.push('drainage');
    if (growing.rainProtection.kind === 'unknown' || growing.rainProtection.value === null) missing.push('rain protection');
    if (growing.sunlightHours.kind === 'unknown' || growing.sunlightHours.value === null) missing.push('sun exposure');
    if (growing.irrigation.kind === 'unknown' || growing.irrigation.value === null) missing.push('water provision');
    if (missing.length) return { ...result, reason: `DOA lists ${plant.cultivar} for lowlands; ${missing.join(', ')} on this site still need evidence.` };
    // Reviewed cultivar context does not establish a complete parcel-specific flowering assessment.
    return { ...result, status: 'partly', evidence: 'estimate', reason: `The lowland cultivar guidance and supplied site facts support further assessment. A local grower still needs to assess the planting site and method.` };
  }
  if (plant.key === 'chrysanthemums') {
    result.sourceUrls = [CHRYSANTHEMUMS];
    result.reason = environment.value === 'highland' && environment.kind !== 'unknown'
      ? 'Cooler growing context is relevant to DOA guidance, but temperature, cultivation method and cultivar evidence are still required.'
      : 'DOA guidance describes cooler protected cultivation; lowland suitability cannot be assumed.';
    return result;
  }
  if (['orchids', 'mokara', 'dendrobium'].includes(plant.key)) {
    result.sourceUrls = [ORCHIDS];
    result.reason = 'DOA describes Malaysian orchid cultivation, but genus, growing method and the conditions at this site must match; broad orchid requests do not count as a pass.';
  }
  return result;
}
